import {
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import { type Wallpaper, wallpapersCollection } from '../schema';

export interface WallpaperQuery {
  category?: string;
  searchTerm?: string;
  limitCount?: number;
  lastDoc?: DocumentSnapshot;
}

/**
 * Service for querying, searching, and filtering wallpapers
 */
export class WallpaperQueryService {
  /**
   * Get wallpapers with optional filtering, search, and pagination
   */
  async getWallpapers(queryParams: WallpaperQuery = {}): Promise<{
    wallpapers: Wallpaper[];
    lastDoc: DocumentSnapshot | null;
  }> {
    const { category, searchTerm, limitCount = 20, lastDoc } = queryParams;
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];

    // Add category filter
    if (category && category !== 'all') {
      constraints.push(where('category', '==', category));
    }

    // Add search filter
    if (searchTerm) {
      constraints.push(where('searchKeywords', 'array-contains-any',
        searchTerm.toLowerCase().split(' ')));
    }

    // Add limit
    constraints.push(limit(limitCount));

    // Add pagination
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    const q = query(wallpapersCollection, ...constraints);
    const querySnapshot = await getDocs(q);

    const wallpapers: Wallpaper[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      wallpapers.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Wallpaper);
    });

    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return {
      wallpapers,
      lastDoc: lastVisible,
    };
  }

  /**
   * Get a single wallpaper by ID
   */
  async getWallpaperById(id: string): Promise<Wallpaper | null> {
    const docRef = doc(wallpapersCollection, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Wallpaper;
    }

    return null;
  }

  /**
   * Search wallpapers by term
   */
  async searchWallpapers(searchTerm: string, limitCount = 20): Promise<Wallpaper[]> {
    const result = await this.getWallpapers({ searchTerm, limitCount });
    return result.wallpapers;
  }

  /**
   * Get wallpapers by category
   */
  async getWallpapersByCategory(category: string, limitCount = 20): Promise<Wallpaper[]> {
    const result = await this.getWallpapers({ category, limitCount });
    return result.wallpapers;
  }
}

export const wallpaperQueryService = new WallpaperQueryService();